/**
 * Router-level guards for TanStack Router `beforeLoad`.
 *
 * The admin routes used to guard inside their component with
 * `<Navigate to="/" />`, which renders the (potentially data-fetching) page
 * for a frame before bouncing. `requireAdmin` moves the check to `beforeLoad`
 * so a non-admin is redirected before the route component ever mounts —
 * mirroring the product guard already used by `campaigns.tsx`.
 *
 * Admin state lives in the Better-Auth session (`user.role === "admin"`, the
 * same derivation `useAuth().isAdmin` uses). `beforeLoad` has no synchronous
 * router-context auth, so we read it via the async `authClient.getSession()`.
 * The backend independently enforces admin on every one of these routes (403),
 * so this is a UX guard, not the security boundary.
 */

import { redirect } from "@tanstack/react-router"
import { authClient } from "./auth-client"

/**
 * Throws a redirect to the dashboard unless the current session belongs to a
 * platform super-admin. Use as a route `beforeLoad`.
 */
export async function requireAdmin(): Promise<void> {
  const { data } = await authClient.getSession()
  const role = (data?.user as Record<string, unknown> | undefined)?.role
  if (role !== "admin") {
    throw redirect({ to: "/" })
  }
}

/**
 * Throws a redirect to the dashboard unless the current session belongs to a
 * platform super-admin OR an owner/admin of the active organization. Use as a
 * route `beforeLoad`.
 *
 * The global super-admin role lives in the Better-Auth session (same read as
 * `requireAdmin`); the per-org role does not, so for a non-super-admin we fetch
 * the active member (`authClient.organization.getActiveMember()`) and admit
 * owner/admin. The backend independently enforces the same rule on every
 * manage/mutate route, so this is a UX guard, not the security boundary.
 */
export async function requireCompanyManager(): Promise<void> {
  const { data } = await authClient.getSession()
  const globalRole = (data?.user as Record<string, unknown> | undefined)?.role
  if (globalRole === "admin") return
  const { data: member } = await authClient.organization.getActiveMember()
  const orgRole = member?.role
  if (orgRole === "owner" || orgRole === "admin") return
  throw redirect({ to: "/" })
}
